import { useState, useEffect } from 'react'
import { api } from '../../lib/api'
import { Search, Package, Calendar, DollarSign, User, Store, Phone, Eye, ChevronLeft, ChevronRight, CheckCircle, X, Mail } from 'lucide-react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import AdminNavHeader from '../../components/AdminNavHeader'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface Order {
  id: string
  store_id: string
  customer_name: string
  customer_phone: string
  customer_email?: string
  status: 'pending' | 'preparing' | 'ready' | 'delivering' | 'delivered' | 'cancelled'
  total_amount: string
  delivery_fee: string
  created_at: string
  updated_at: string
  store?: {
    name: string
    slug: string
  }
  items?: Array<{
    id: string
    quantity: number
    unit_price: string
    product: {
      name: string
      image_url?: string
    }
  }>
}

const AdminOrders = () => {
  const navigate = useNavigate()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateFilter, setDateFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalOrders, setTotalOrders] = useState(0)
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    preparing: 0,
    ready: 0,
    delivering: 0,
    delivered: 0,
    cancelled: 0,
    totalRevenue: 0
  })
  const ordersPerPage = 20

  useEffect(() => {
    loadOrders()
    loadStats()
  }, [currentPage, statusFilter, dateFilter])

  const loadOrders = async () => {
    const cacheKey = `admin_orders_${currentPage}_${statusFilter}_${dateFilter}`
    
    // Intentar cargar desde cache primero
    const cachedOrders = getCacheStale<Order[]>(cacheKey)
    if (cachedOrders && cachedOrders.length > 0) {
      setOrders(cachedOrders)
      setLoading(false)
    }

    try {
      setLoading(true)

      const response = await api.getAdminOrders({
        page: currentPage.toString(),
        limit: ordersPerPage.toString(),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(dateFilter !== 'all' && { dateFilter })
      })
      const ordersData = response.data || []
      setOrders(ordersData)
      setTotalPages(response.pagination?.totalPages || 1)
      setTotalOrders(response.pagination?.total || 0)
      
      // Guardar en cache (2 minutos de TTL - órdenes muy dinámicas)
      setCache(cacheKey, ordersData, 2 * 60 * 1000)
    } catch (error: any) {
      console.error('Error loading orders:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrders) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar los pedidos')
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    const cacheKey = 'admin_order_stats'
    
    // Intentar cargar desde cache primero
    const cachedStats = getCacheStale<typeof stats>(cacheKey)
    if (cachedStats) {
      setStats(cachedStats)
    }

    try {
      const response = await api.getAdminOrderStats()
      if (response.success) {
        setStats(response.data)
        
        // Guardar en cache (2 minutos de TTL - estadísticas cambian frecuentemente)
        setCache(cacheKey, response.data, 2 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading order stats:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStats) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
    }
  }

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending': return 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 border-2 border-yellow-300'
      case 'preparing': return 'bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 border-2 border-blue-300'
      case 'ready': return 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-800 border-2 border-purple-300'
      case 'delivering': return 'bg-gradient-to-r from-indigo-100 to-indigo-50 text-indigo-800 border-2 border-indigo-300'
      case 'delivered': return 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border-2 border-green-300'
      case 'cancelled': return 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 border-2 border-red-300'
      default: return 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 border-2 border-gray-300'
    }
  }

  const getStatusLabel = (status: Order['status']) => {
    switch (status) {
      case 'pending': return 'Pendiente'
      case 'preparing': return 'Preparando'
      case 'ready': return 'Listo'
      case 'delivering': return 'En camino'
      case 'delivered': return 'Entregado'
      case 'cancelled': return 'Cancelado'
      default: return status
    }
  }

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_phone.includes(searchTerm) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.store?.name.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesSearch
  })

  if (loading && orders.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Cargando pedidos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 pb-20 sm:pb-6">
      {/* Header */}
      <AdminNavHeader
        title="Gestión de Pedidos"
        subtitle={`${totalOrders} pedidos totales`}
      />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-6">
        {/* Stats Cards - Mejorados */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-5 md:mb-6">
          <div className="group bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 p-3 sm:p-4 md:p-5 overflow-hidden hover:shadow-2xl hover:shadow-gray-900/10 transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-gray-600 uppercase tracking-wide mb-0.5 sm:mb-1">Total</p>
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="group bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 p-3 sm:p-4 md:p-5 overflow-hidden hover:shadow-2xl hover:shadow-gray-900/10 transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-gray-600 uppercase tracking-wide mb-0.5 sm:mb-1">Pendientes</p>
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="group bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 p-3 sm:p-4 md:p-5 overflow-hidden hover:shadow-2xl hover:shadow-gray-900/10 transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-gray-600 uppercase tracking-wide mb-0.5 sm:mb-1">Ingresos</p>
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 leading-tight">RD${stats.totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="group bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 p-3 sm:p-4 md:p-5 overflow-hidden hover:shadow-2xl hover:shadow-gray-900/10 transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-gray-600 uppercase tracking-wide mb-0.5 sm:mb-1">Entregados</p>
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">{stats.delivered}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters - Mejorados */}
        <div className="bg-white/80 backdrop-blur-2xl rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 p-3 sm:p-4 md:p-6 mb-4 sm:mb-5 md:mb-6">
          {/* Search Bar */}
          <div className="mb-3 sm:mb-4">
            <div className="relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar pedidos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 sm:pl-12 md:pl-14 pr-10 sm:pr-12 py-2.5 sm:py-3 md:py-3.5 bg-white border-2 border-gray-200 rounded-lg sm:rounded-xl md:rounded-2xl focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all duration-200 text-xs sm:text-sm md:text-base font-medium shadow-sm hover:shadow-md placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors active:scale-95 p-1"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="flex-1 sm:flex-shrink-0 min-w-0 sm:min-w-[140px] md:min-w-[160px] px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 bg-white border-2 border-gray-200 rounded-lg sm:rounded-xl md:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="preparing">Preparando</option>
              <option value="ready">Listo</option>
              <option value="delivering">En camino</option>
              <option value="delivered">Entregado</option>
              <option value="cancelled">Cancelado</option>
            </select>

            <select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="flex-1 sm:flex-shrink-0 min-w-0 sm:min-w-[140px] md:min-w-[160px] px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 bg-white border-2 border-gray-200 rounded-lg sm:rounded-xl md:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <option value="all">Todas las fechas</option>
              <option value="today">Hoy</option>
              <option value="week">Esta semana</option>
              <option value="month">Este mes</option>
            </select>

            {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setStatusFilter('all')
                  setDateFilter('all')
                  setCurrentPage(1)
                }}
                className="w-full sm:w-auto flex-shrink-0 px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg sm:rounded-xl md:rounded-2xl text-xs sm:text-sm font-bold hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 min-h-[44px] sm:min-h-0"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Orders List - Mejorado */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 p-8 sm:p-12 md:p-16 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 md:mb-6 shadow-lg">
              <Package className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-gray-400" />
            </div>
            <h3 className="text-base sm:text-lg md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">No hay pedidos</h3>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 font-medium px-4">
              {searchTerm || statusFilter !== 'all' || dateFilter !== 'all'
                ? 'No se encontraron pedidos con los filtros seleccionados'
                : 'Aún no hay pedidos registrados'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4 md:space-y-5">
            {filteredOrders.map((order) => (
              <div key={order.id} className="group bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 overflow-hidden hover:shadow-2xl hover:shadow-gray-900/10 transition-all duration-300 hover:-translate-y-1">
                <div className="p-3 sm:p-4 md:p-6">
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start sm:items-center gap-2 sm:gap-3 mb-2 sm:mb-3 flex-wrap">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                          <Package className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 truncate">Pedido #{order.id.slice(-8)}</h3>
                          {order.store && (
                            <div className="flex items-center text-[10px] sm:text-xs md:text-sm text-gray-600 mt-0.5 sm:mt-1">
                              <Store className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 flex-shrink-0" />
                              <span className="truncate">{order.store.name}</span>
                            </div>
                          )}
                        </div>
                        <span className={`inline-flex items-center px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-full text-[10px] sm:text-xs md:text-sm font-bold ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-lg sm:text-xl md:text-2xl font-bold text-primary-600 mb-0.5 sm:mb-1">RD${parseFloat(order.total_amount).toFixed(2)}</p>
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('es-DO', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Customer Info - Mejorado */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-lg sm:rounded-xl md:rounded-2xl p-2.5 sm:p-3 md:p-4 mb-3 sm:mb-4 border-2 border-gray-200/60">
                    <div className="space-y-2">
                      <div className="flex items-center text-xs sm:text-sm md:text-base text-gray-700">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md mr-2 sm:mr-3 flex-shrink-0">
                          <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white" />
                        </div>
                        <span className="font-bold text-gray-900 truncate">{order.customer_name}</span>
                      </div>
                      <div className="flex items-center text-[10px] sm:text-xs md:text-sm text-gray-600">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md mr-2 sm:mr-3 flex-shrink-0">
                          <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white" />
                        </div>
                        <span className="truncate">{order.customer_phone}</span>
                      </div>
                      {order.customer_email && (
                        <div className="flex items-center text-[10px] sm:text-xs md:text-sm text-gray-600">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md mr-2 sm:mr-3 flex-shrink-0">
                            <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white" />
                          </div>
                          <span className="truncate">{order.customer_email}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Order Items - Mejorado */}
                  {order.items && order.items.length > 0 && (
                    <div className="mb-3 sm:mb-4">
                      <p className="text-[10px] sm:text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">Productos:</p>
                      <div className="space-y-1.5 sm:space-y-2">
                        {order.items.slice(0, 3).map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-2 sm:p-2.5 md:p-3 bg-gradient-to-r from-primary-50/50 to-primary-100/30 rounded-lg sm:rounded-xl md:rounded-2xl border border-primary-200/50">
                            <span className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-700 truncate pr-2">
                              {item.quantity}x {item.product.name}
                            </span>
                            <span className="text-[10px] sm:text-xs md:text-sm font-bold text-primary-600 flex-shrink-0">RD${parseFloat(item.unit_price).toFixed(2)}</span>
                          </div>
                        ))}
                        {order.items.length > 3 && (
                          <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 font-medium text-center py-1.5 sm:py-2">+{order.items.length - 3} producto{order.items.length - 3 > 1 ? 's' : ''} más</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Footer - Mejorado */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 pt-3 sm:pt-4 border-t-2 border-gray-200/60">
                    <div className="text-[10px] sm:text-xs md:text-sm text-gray-600 font-medium">
                      Entrega: <span className="font-bold text-gray-900">RD${parseFloat(order.delivery_fee || '0').toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Quick Status Change */}
                      {order.status === 'ready' && (
                        <button
                          onClick={async () => {
                            if (confirm('¿Marcar como entregado?')) {
                              try {
                                const response = await api.patch(`/orders/${order.id}/status`, { status: 'delivered' })
                                if (response.data.success) {
                                  toast.success('Estado actualizado')
                                  loadOrders()
                                }
                              } catch (error: any) {
                                toast.error(error.response?.data?.message || 'Error al actualizar estado')
                              }
                            }
                          }}
                          className="flex items-center px-2.5 sm:px-3 md:px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg sm:rounded-xl md:rounded-2xl text-[10px] sm:text-xs md:text-sm font-bold hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg active:scale-95 min-h-[44px] sm:min-h-0"
                        >
                          <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                          <span className="hidden sm:inline">Entregado</span>
                          <span className="sm:hidden">Entregar</span>
                        </button>
                      )}
                      {order.status === 'preparing' && (
                        <button
                          onClick={async () => {
                            if (confirm('¿Marcar como listo?')) {
                              try {
                                const response = await api.patch(`/orders/${order.id}/status`, { status: 'ready' })
                                if (response.data.success) {
                                  toast.success('Estado actualizado')
                                  loadOrders()
                                }
                              } catch (error: any) {
                                toast.error(error.response?.data?.message || 'Error al actualizar estado')
                              }
                            }
                          }}
                          className="flex items-center px-2.5 sm:px-3 md:px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg sm:rounded-xl md:rounded-2xl text-[10px] sm:text-xs md:text-sm font-bold hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg active:scale-95 min-h-[44px] sm:min-h-0"
                        >
                          <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                          <span className="hidden sm:inline">Listo</span>
                          <span className="sm:hidden">Listo</span>
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/admin/orders/${order.id}`)}
                        className="flex items-center px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg sm:rounded-xl md:rounded-2xl text-[10px] sm:text-xs md:text-sm font-bold hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg active:scale-95 min-h-[44px] sm:min-h-0"
                      >
                        <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 mr-1 sm:mr-1.5" />
                        <span className="hidden sm:inline">Ver Detalles</span>
                        <span className="sm:hidden">Detalles</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination - Mejorado */}
        {totalPages > 1 && (
          <div className="mt-4 sm:mt-6 md:mt-8 flex items-center justify-center gap-2 sm:gap-3 md:gap-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-2.5 sm:p-3 md:p-4 bg-white border-2 border-gray-200 rounded-lg sm:rounded-xl md:rounded-2xl text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-primary-300 transition-all duration-200 active:scale-95 shadow-md hover:shadow-lg min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </button>
            <span className="text-xs sm:text-sm md:text-base font-bold text-gray-700 bg-white px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl md:rounded-2xl shadow-md border-2 border-gray-200">
              {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-2.5 sm:p-3 md:p-4 bg-white border-2 border-gray-200 rounded-lg sm:rounded-xl md:rounded-2xl text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-primary-300 transition-all duration-200 active:scale-95 shadow-md hover:shadow-lg min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminOrders
